<template>
  <div class="student-id-container">
    <h2 class="section-title">Enter Your Student ID</h2>
    
    <!-- Priority Notice Banner -->
    <div v-if="isPriority" class="priority-notice">
      <i class="fas fa-star me-2"></i> Priority Transaction
    </div>
    
    <div class="student-id-form">
      <div class="form-group">
        <label for="studentId" class="form-label">Student ID Number</label>
        <div class="input-group mb-3">
          <input
            type="text"
            class="form-control form-control-lg"
            id="studentId"
            v-model="studentId"
            placeholder="Enter your student ID"
            :disabled="loading"
            @keyup.enter="verifyStudent"
          />
        </div>
        <small class="form-text text-muted">Please enter your complete student ID number</small>
      </div>
      
      <div v-if="error" class="alert alert-danger mt-3">
        {{ error }}
      </div>
      
      <div class="action-buttons mt-3">
        <button
          class="btn btn-primary btn-lg w-100"
          type="button"
          @click="verifyStudent"
          :disabled="loading || !studentId"
        >
          <span v-if="loading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          {{ loading ? 'Verifying...' : 'Continue' }}
        </button>
      </div>
      
      <div class="virtual-keyboard mt-4">
        <div class="keyboard-row">
          <button class="key" @click="appendToId('1')">1</button>
          <button class="key" @click="appendToId('2')">2</button>
          <button class="key" @click="appendToId('3')">3</button>
        </div>
        <div class="keyboard-row">
          <button class="key" @click="appendToId('4')">4</button>
          <button class="key" @click="appendToId('5')">5</button>
          <button class="key" @click="appendToId('6')">6</button>
        </div>
        <div class="keyboard-row">
          <button class="key" @click="appendToId('7')">7</button>
          <button class="key" @click="appendToId('8')">8</button>
          <button class="key" @click="appendToId('9')">9</button>
        </div>
        <div class="keyboard-row">
          <button class="key" @click="appendToId('-')">-</button>
          <button class="key" @click="appendToId('0')">0</button>
          <button class="key key-delete" @click="deleteLastDigit()">
            <i class="fas fa-backspace"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'StudentId',
  props: {
    config: {
      type: Object,
      default: () => ({})
    },
    transactionData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      localError: null,
      // Using computed property for studentId to store in sessionStorage
      lastStudentId: '',
      loading: false
    };
  },
  computed: {
    ...mapGetters({
      storeLoading: 'kiosk/isLoading',
      storeError: 'kiosk/getError',
      isPriority: 'kiosk/isPriorityMode'
    }),
    error() {
      return this.localError || this.storeError;
    },
    studentId: {
      get() {
        // Try to get from session storage if empty
        if (!this.lastStudentId) {
          const stored = sessionStorage.getItem('kiosk_student_id');
          if (stored) {
            return stored;
          }
        }
        return this.lastStudentId;
      },
      set(value) {
        this.lastStudentId = value;
        // Store in session storage
        sessionStorage.setItem('kiosk_student_id', value);
      }
    }
  },
  created() {
    // Initialize lastStudentId from session storage or transactionData
    if (this.transactionData && this.transactionData.studentId) {
      this.lastStudentId = this.transactionData.studentId;
    } else {
    const stored = sessionStorage.getItem('kiosk_student_id');
    if (stored) {
      this.lastStudentId = stored;
    }
    }
    
    // Check if we have the required data from previous steps
    this.$nextTick(() => {
      const store = this.$store;
      if (!store.getters['kiosk/getSelectedTransactionType']) {
        console.warn('No transaction type selected');
        this.$emit('step-back');
      }
    });
  },
  methods: {
    ...mapActions({
      fetchStudentByIdNumber: 'kiosk/fetchStudentByIdNumber'
    }),
    appendToId(char) {
      // Allow digits and manual dash input only
      if (!/^[0-9-]$/.test(char)) return;
      this.studentId += char;
    },
    clearId() {
      this.studentId = '';
    },
    deleteLastDigit() {
      // Remove only the last character, regardless of type
      this.studentId = this.studentId.slice(0, -1);
    },
    async verifyStudent() {
      if (!this.studentId) {
        this.localError = 'Please enter a student ID';
        return;
      }
      
      try {
        this.localLoading = true;
        this.localError = null;
        
        // Store the student ID in session storage for future use
        sessionStorage.setItem('kiosk_student_id', this.studentId);
        
        // Verify student exists before proceeding
        try {
          const studentData = await this.$store.dispatch('kiosk/fetchStudentByIdNumber', this.studentId);
          
          if (!studentData) {
            this.localError = 'Student not found. Please check the ID and try again.';
            this.localLoading = false;
            return;
          }
          
          // Emit step-complete event with student ID
          this.$emit('step-complete', {
            studentId: this.studentId
          });
        } catch (error) {
          console.error('Student verification error:', error);
          this.localError = 'Student not found. Please check the ID and try again.';
        }
      } catch (error) {
        console.error('Student verification error:', error);
        this.localError = 'Failed to verify student. Please try again.';
      } finally {
        this.localLoading = false;
      }
    }
  }
};
</script>

<style scoped>
.student-id-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1rem;
  scale: 1.5;
}

.section-title {
  text-align: center;
  margin-bottom: 2rem;
  color: #2C3E50;
}

.priority-notice {
  background-color: #28a745;
  color: white;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  width: 100%;
  max-width: 500px;
  text-align: center;
  font-size: 1.3rem;
  font-weight: 600;
  box-shadow: 0 4px 8px rgba(40, 167, 69, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
}

.student-id-form {
  width: 100%;
  max-width: 500px;
}

.form-control-lg {
  font-size: 1.5rem;
  padding: 0.8rem 1rem;
}

.virtual-keyboard {
  margin-top: 2rem;
  width: 100%;
  max-width: 360px; /* Set fixed width for 3x4 layout */
  margin-left: auto;
  margin-right: auto;
}

.keyboard-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 15px;
}

.key {
  width: 80px;
  height: 80px;
  margin: 0 5px;
  font-size: 1.8rem;
  border-radius: 10px;
  background-color: #f8f9fa;
  border: 1px solid #ced4da;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
}

.key:hover {
  background-color: #e9ecef;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.key:active {
  background-color: #dee2e6;
  transform: translateY(1px);
  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
}

.key-clear {
  background-color: #ffc107;
  color: #212529;
  font-size: 1.2rem;
}

.key-delete {
  background-color: #6c757d;
  color: white;
}

/* Portrait mode adjustments */
@media screen and (orientation: portrait) {
  .section-title {
    font-size: 2.4rem;
    margin-bottom: 3rem;
  }
  
  .priority-notice {
    font-size: 1.8rem;
    padding: 1.5rem;
    border-radius: 12px;
    margin-bottom: 2rem;
    max-width: 700px;
  }
  
  .student-id-form {
    max-width: 700px;
  }
  
  .form-control-lg {
    font-size: 2rem;
    padding: 1rem 1.5rem;
    height: auto;
  }
  
  .virtual-keyboard {
    margin-top: 3rem;
    max-width: 460px;
  }
  
  .keyboard-row {
    margin-bottom: 20px;
  }
  
  .key {
    width: 120px;
    height: 120px;
    font-size: 2.4rem;
    margin: 0 6px;
    border-radius: 15px;
  }
  
  .key-clear {
    font-size: 1.8rem;
  }
  
  .key-delete {
    font-size: 1.8rem;
  }
}
</style> 